//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import { Checkbox } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import LineItemActions from 'common/redux/actions/lineItem';
import AppPreferencesActions from 'common/redux/actions/appPreferences';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import ToastActions from 'common/redux/actions/toast';
import LineItemSelectors from 'common/redux/selectors/lineItem';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import {
    ConnectedSkillSelectorFilter,
    ConnectedSkillIdSelectorFilter
} from 'common/entity/components/SkillSelectorFilter';
import SkillSelectors from 'common/redux/selectors/skill';
import { withRouter } from 'common/components/core/router';
import { withUnitConverter } from 'common/containers/intl';
import { TemperatureType } from 'common/models/internal/TemperatureThreshold';
import SingleLineItemEditContainer from './SingleLineItemEditContainer';
import LineItemMasterImportContainer from './LineItemMasterImportContainer';

import theme from 'common/entity/BulkEditContainer.scss';
import specificTheme from './LineItemMasterContainer.scss';
import SkillActions from 'common/redux/actions/skill';

import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { Volume, VolumeUnitType } from 'common/models/internal/Volume';
import {
    sourceUnitForWeight,
    preferredUnitsForWeight,
    sourceUnitForDistance,
    preferredUnitsForDistance
} from 'common/models/internal/Dimensions';
import Units from 'common/enums/UnitEnum';
import NumberUtils from 'common/utils/NumberUtils';
import { dashboardColors } from 'common/enums/ColorEnum';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import { ListWithHeader } from 'common/components/core/list/ListWithHeader';
import { tableButtonWithPopoverFactory } from 'common/components/common/TableButtonWithPopover';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';

import type { MasterLineItem } from 'common/models/internal/MasterLineItem';
import { LineItemPart } from 'common/models/internal/LineItemPart';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import type { List } from 'immutable';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';

import VisitUtils from 'common/utils/VisitUtils';
import SkillsList from 'common/components/core/common/SkillsList';
import { RoleOperationEnum } from 'common/modules/rbac';

const ENTITY_TABLE_ID = 'LineItemMaster';
const ENTITY_NAME_ID = 'masterLineItem';
const ENTITY_EXPORT_ID = 'SKU_MASTER';
type TdPropsType = {
    rest: {
        t: TFunction,
        theme: Object,
        unitConverter: UnitConversionHelper
    }
};

const selectSkillsId = ({ id }) => id;

const Notes = ({ theme, t, list, title }) => {
    return (
        <ListWithHeader className={theme.notesList} theme={theme} title={title}>
            {list.map((instruction, index) => {
                return (
                    <div key={index} className={theme.note}>
                        <span className={theme.title}>
                            {t('skuTable.noteCount', { num: index + 1 })}
                        </span>
                        <span className={theme.text}>{instruction}</span>
                    </div>
                );
            })}
        </ListWithHeader>
    );
};

const Parts = ({ theme, t, list, title, unitConverter }) => {
    const renderVolumePart = (volume: Volume) => {
        return renderVolume(volume, t, unitConverter);
    };

    return (
        <ListWithHeader className={theme.partsList} theme={theme} title={title}>
            {(list: List<LineItemPart>).toArray().map((part, index) => {
                const { weight, volume, dimensions } = part;
                const { length, breadth, height } = dimensions || {};
                return (
                    <div key={index} className={theme.partWrap}>
                        <span className={theme.partTitle}>
                            {t('skuTable.sectionPart', {
                                count: index + 1
                            })}
                        </span>
                        <div className={theme.part}>
                            {dimensions && !dimensions.isEmpty() ? (
                                <div className={theme.dimensionsValue}>
                                    <div className={theme.dimension}>
                                        <span className={theme.title}>
                                            {t('dimensionsInput.length')}
                                        </span>
                                        <span
                                            title={renderVolumePart(volume)}
                                            className={theme.text}
                                        >
                                            {length ? (
                                                <span>
                                                    {renderDistance(
                                                        length,
                                                        unitConverter
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </span>
                                    </div>
                                    <div className={theme.dimension}>
                                        <span className={theme.title}>
                                            {t('dimensionsInput.breadth')}
                                        </span>
                                        <span
                                            title={renderVolumePart(volume)}
                                            className={theme.text}
                                        >
                                            {breadth ? (
                                                <span>
                                                    {renderDistance(
                                                        breadth,
                                                        unitConverter
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </span>
                                    </div>
                                    <div className={theme.dimension}>
                                        <span className={theme.title}>
                                            {t('dimensionsInput.height')}
                                        </span>
                                        <span
                                            title={renderVolumePart(volume)}
                                            className={theme.text}
                                        >
                                            {height ? (
                                                <span>
                                                    {renderDistance(
                                                        height,
                                                        unitConverter
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </span>
                                    </div>
                                </div>
                            ) : null}
                            <div className={theme.volume}>
                                <span className={theme.title}>
                                    {t('skuTable.volume')}
                                </span>
                                <span
                                    title={renderVolumePart(volume)}
                                    className={theme.text}
                                >
                                    {renderVolumePart(volume)}
                                </span>
                            </div>
                            <div className={theme.weight}>
                                <span className={theme.title}>
                                    {t('skuTable.weight')}
                                </span>
                                <span className={theme.text}>
                                    {weight !== null
                                        ? renderWeight(weight, unitConverter)
                                        : '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </ListWithHeader>
    );
};

const NotesButtonWithPopover = tableButtonWithPopoverFactory({
    ListElement: Notes
});

const PartsButtonWithPopover = tableButtonWithPopoverFactory({
    ListElement: Parts
});

const getSkillIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { values, allowEmptyOrNull }
    } = filteredColumn;
    const skillsFilter = values.map(value => ({
        clientId,
        id: value
    }));
    return new FilterBuilder()
        .withName('skills')
        .withOperation(FilterOperation.Contains)
        .withValues(skillsFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull);
};

const COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'skuTable.id',
        accessor: 'id',
        frozen: true,
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'skuTable.code',
        id: 'code',
        accessor: 'code',
        frozen: true,
        minWidth: 100,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'skuTable.name',
        accessor: 'name'
    },
    {
        headerTextId: 'skuTable.description',
        accessor: 'description'
    },
    {
        headerTextId: 'skuTable.scanId',
        accessor: 'scanId'
    },
    {
        headerTextId: 'skuTable.category',
        id: 'category',
        accessor: 'category',
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const { original } = props;
            const { category } = original;
            if (!category) {
                return '-';
            }
            return category;
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { entitySettings } = column.getHeaderProps();
            if (!entitySettings) {
                return [];
            }
            const { lineItemSettings } = (entitySettings: EntitySettings);
            let optionsForSelect = [];
            if (entitySettings) {
                optionsForSelect = lineItemSettings.getOptionsForSelect();
            }
            return optionsForSelect;
        }
    },
    {
        headerTextId: 'skuTable.instructions',
        accessor: 'instructions',
        filterable: false,
        sortable: false,
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const { instructions } = original;
            if (instructions.size === 0) {
                return '-';
            }

            return (
                <NotesButtonWithPopover
                    ripple={false}
                    popOverClassName={theme.notesListPopover}
                    buttonTooltip={t('liveView:clickForMoreDetails')}
                    list={instructions}
                    listTitle={t('skuTable.instructions')}
                    theme={theme}
                    defaultDataGrouped
                    className={classnames(theme.tableButton, theme.plain)}
                    t={t}
                >
                    {t('skuTable.viewNotes', {
                        count: instructions.size
                    })}
                </NotesButtonWithPopover>
            );
        }
    },
    {
        headerTextId: 'skuTable.totalPartVolume',
        id: 'totalVolume',
        accessor: 'summary.totalVolume.value',
        width: 150,
        filterable: true,
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { t, unitConverter }
                }
            } = props;
            const totalVolume = original.summary
                ? original.summary.totalVolume
                : {};
            return renderVolume(totalVolume, t, unitConverter);
        }
    },
    {
        headerTextId: 'skuTable.totalPartWeight',
        id: 'totalWeight',
        accessor: 'summary.totalWeight',
        width: 150,
        filterable: true,
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { unitConverter }
                }
            } = props;
            const totalWeight = original.summary
                ? original.summary.totalWeight
                : null;
            return renderWeight(totalWeight, unitConverter);
        }
    },
    {
        headerTextId: 'skuTable.parts',
        accessor: 'parts',
        width: 210,
        filterable: false,
        sortable: false,
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { t, theme, unitConverter }
                }
            } = props;
            const { parts } = original;
            if (parts.size === 0) {
                return '-';
            }

            if (parts.size === 1) {
                const part = parts.first();
                const { volume, weight, dimensions } = part;
                const { length, breadth, height } = dimensions || {};
                return (
                    <span className={theme.cellText}>
                        {t('skuTable.volume', { context: 'short' })}
                        :&nbsp;
                        {renderVolume(volume, t, unitConverter)}
                        {weight
                            ? `, ${t('skuTable.weight', {
                                  context: 'short'
                              })}: `
                            : null}
                        {weight ? renderWeight(weight, unitConverter) : null}
                        {dimensions && !dimensions.isEmpty() ? (
                            <span>
                                {`, ${t('skuTable.dimensions', {
                                    context: 'short'
                                })}:  ${renderDistance(length, unitConverter)} x
                                ${renderDistance(breadth, unitConverter)} x
                                ${renderDistance(height, unitConverter)}`}
                            </span>
                        ) : null}
                    </span>
                );
            }

            return (
                <PartsButtonWithPopover
                    ripple={false}
                    popOverClassName={theme.notesListPopover}
                    buttonTooltip={t('liveView:clickForMoreDetails')}
                    list={parts}
                    listTitle={t('skuTable.parts')}
                    theme={theme}
                    defaultDataGrouped
                    className={classnames(theme.tableButton, theme.plain)}
                    preferPlace={PopoverPlaceEnum.Left}
                    t={t}
                    unitConverter={unitConverter}
                >
                    {t('skuTable.viewParts', {
                        count: parts.size
                    })}
                </PartsButtonWithPopover>
            );
        }
    },
    {
        headerTextId: 'skuTable.tat',
        id: 'transactionDuration',
        property: 'transactionDuration',
        filterable: false,
        dataType: CellDataTypes.HumanizedTime,
        accessor: 'transactionDuration',
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'skuTable.isCrateable',
        id: 'isCrateable',
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('skuTable.isCrateableText') },
                { value: false, label: t('skuTable.isNotCrateableText') }
            ];
            return optionsForSelect;
        },
        accessor: 'shouldCrate',
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { shouldCrate } = original;
            return shouldCrate
                ? t('skuTable.isCrateableText')
                : t('skuTable.isNotCrateableText');
        }
    },
    {
        headerTextId: 'skuTable.cartonCapacity',
        accessor: 'carton.capacity'
    },
    {
        headerTextId: 'skuTable.cartonWeight',
        accessor: 'carton.weight',
        id: 'cartonWeight',
        filterable: false,
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { unitConverter }
                }
            } = props;
            const { carton } = original;
            if (!carton) {
                return '-';
            }
            const { weight } = carton;
            if (!weight) {
                return '-';
            }
            return renderWeight(weight, unitConverter);
        }
    },
    {
        headerTextId: 'skuTable.cartonVolume',
        fieldId: 'carton.volume',
        id: 'cartonVolume',
        sortable: false,
        filterable: false,
        Cell: (props: { original: MasterLineItem, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { t, unitConverter }
                }
            } = props;
            const { carton } = original;
            if (!carton) {
                return '-';
            }
            const { volume } = carton;
            if (!volume) {
                return '-';
            }
            return renderVolume(volume, t, unitConverter);
        }
    },
    {
        headerTextId: 'skuTable.skillId',
        exportId: 'skills.id',
        sortable: false,
        id: 'skills.id',
        accessor: 'skills',
        FilterComponent: ConnectedSkillIdSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original: { skills: skillIds },
                tdProps: {
                    rest: { skills }
                }
            } = props;
            return (
                <SkillsList
                    skills={skills}
                    skillIds={skillIds}
                    labelSelector={selectSkillsId}
                />
            );
        }
    },
    {
        headerTextId: 'skuTable.skillName',
        exportId: 'skills.name',
        sortable: false,
        id: 'skills.name',
        accessor: 'skills',
        FilterComponent: ConnectedSkillSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original: { skills: skillIds },
                tdProps: {
                    rest: { skills }
                }
            } = props;
            return <SkillsList skills={skills} skillIds={skillIds} />;
        }
    },
    {
        headerTextId: 'skuTable.manufacturingLocations',
        id: 'manufacturingLocations',
        fieldId: 'locations',
        filterable: false,
        sortable: false,
        Cell: (props: { original: MasterLineItem }) => {
            const {
                original: { locations }
            } = props;
            if (locations.size === 0) {
                return '-';
            }
            return locations.map(location => location.name).join(', ');
        }
    },
    {
        headerTextId: 'skuTable.minTemp',
        id: 'minTemp',
        accessor: 'temperatureThreshold.lowerThreshold',
        filterable: false
    },
    {
        headerTextId: 'skuTable.maxTemp',
        id: 'maxTemp',
        accessor: 'temperatureThreshold.higherThreshold',
        filterable: false
    },
    {
        headerTextId: 'skuTable.tempType',
        id: 'tempType',
        accessor: 'temperatureThreshold.temperatureType',
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            return Object.values(TemperatureType).map(temperatureType => ({
                value: temperatureType,
                label: t(`skuProperties.temperatureTypes.${temperatureType}`)
            }));
        }
    },
    {
        headerTextId: 'skuTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'skuTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'skuTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'skuTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

export const renderWeight = function (
    weight: number,
    unitConverter: UnitConversionHelper
) {
    return unitConverter.convertValue({
        value: weight,
        sourceUnit: sourceUnitForWeight,
        preferredUnits: preferredUnitsForWeight,
        pluralizeUnit: false
    }).valueString;
};

const renderDistance = function (
    distance: number,
    unitConverter: UnitConversionHelper
) {
    return unitConverter.convertValue({
        value: distance,
        sourceUnit: sourceUnitForDistance,
        preferredUnits: preferredUnitsForDistance,
        pluralizeUnit: false
    }).valueString;
};

export const renderVolume = function (
    volume: Volume,
    t: (string, ?Object) => string,
    unitConverter: UnitConversionHelper
) {
    const { unit, value } = volume;
    if (unit === VolumeUnitType.ItemCount) {
        if (!NumberUtils.isNumeric(value)) {
            return '-';
        }
        return t('skuTable.volumeCount', { count: value });
    }
    return unitConverter.convertVolume(value, volume.unit).valueString;
};

const preferredUnitsForTemp = [
    Units.list.temperature.DegreeCelsius,
    Units.list.temperature.DegreeFahrenheit
];
const sourceUnitForTemp = Units.list.temperature.DegreeCelsius;

const getTempPreferredUnitText = function (
    unitConverter: UnitConversionHelper
) {
    return unitConverter.getConvertedUnitDisplayText({
        sourceUnit: sourceUnitForTemp,
        preferredUnits: preferredUnitsForTemp,
        pluralizeUnit: false
    });
};

const convertTempValue = function (
    value: number,
    unitConverter: UnitConversionHelper
) {
    return unitConverter.convertValue({
        value,
        sourceUnit: sourceUnitForTemp,
        preferredUnits: preferredUnitsForTemp
    }).value;
};

const renderTemp = function (
    column: Object,
    unitConverter: UnitConversionHelper
) {
    const preferredUnitText = getTempPreferredUnitText(unitConverter);
    return {
        preferredUnitText,
        Cell: ({ value }) => {
            if (!value || !NumberUtils.isNumeric(value)) {
                return '-';
            }
            return convertTempValue(value, unitConverter);
        }
    };
};

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type LineItemsDataType = {
    lineItems: Array<MasterLineItem>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: LineItemsDataType,
    unitConverter: UnitConversionHelper,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    t: TFunction,
    isGlobalPersonnel: boolean
};

const LineItemMaster = entityMasterFactory({
    EntityEditViewContainer: SingleLineItemEditContainer,
    EntityImportContainer: LineItemMasterImportContainer,
    EntityExportContainer: MasterExportContainer,
    COLUMNS,
    options: {
        entityTableId: 'LineItemMaster',
        operations: {
            edit: RoleOperationEnum.UPDATE_SKU,
            add: RoleOperationEnum.UPDATE_SKU,
            export: RoleOperationEnum.EXPORT_SKU
        }
    }
});

class LineItemMasterContainer extends PureComponent<Props> {
    componentDidMount = () => {
        this.fetchOtherResources();
    };

    componentDidUpdate = (prevProps: Props) => {
        const { clientId } = this.props;
        if (clientId !== prevProps.clientId) {
            this.fetchOtherResources();
        }
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const {
            original: { id }
        } = rowInfo;
        const {
            t,
            entitySettings,
            selectedRows,
            toggleRowSelection,
            unitConverter,
            theme,
            skills,
            isGlobalPersonnel
        } = this.props;
        const isChecked = selectedRows.indexOf(id) >= 0;
        return {
            onCheckboxClick: () => {
                toggleRowSelection(id);
            },
            t,
            entitySettings,
            unitConverter,
            theme,
            isChecked,
            skills,
            className: classnames({ [theme.selectedRow]: isChecked }),
            isGlobalPersonnel
        };
    };

    processColumns = (columns: Array<Object>) => {
        const { unitConverter } = this.props;
        return columns.map((rawColumn: Object) => {
            switch (rawColumn.id) {
                case 'minTemp':
                    return {
                        ...rawColumn,
                        ...renderTemp(rawColumn, unitConverter)
                    };
                case 'maxTemp':
                    return {
                        ...rawColumn,
                        ...renderTemp(rawColumn, unitConverter)
                    };
                default:
                    return rawColumn;
            }
        });
    };

    fetchOtherResources = () => {
        const { clientId, fetchSkills } = this.props;
        fetchSkills({ clientId });
    };

    getAdditionalColumns = selectedColumns => {
        const partsColumnShown = selectedColumns.find(col => col === 'parts');
        return partsColumnShown
            ? [
                  ...selectedColumns,
                  'partVolume',
                  'partWeight',
                  'partLength',
                  'partBreadth',
                  'partHeight',
                  'otherPartsVolume',
                  'otherPartsWeight',
                  'otherPartsLength',
                  'otherPartsBreadth',
                  'otherPartsHeight'
              ]
            : selectedColumns;
    };

    render() {
        const { clientId, t } = this.props;
        return (
            <LineItemMaster
                {...this.props}
                getAdditionalColumns={this.getAdditionalColumns}
                emptyStateImageSrc="/img/line-item-master-empty.png"
                entityNameId={ENTITY_NAME_ID}
                entityExportId={ENTITY_EXPORT_ID}
                entityId={'lineItemId'}
                entitySettingsName={'lineItemSettings'}
                fetchOptions={{ clientId, include: 'LOCATIONS' }}
                getTdProps={this.getTdProps}
                customProcessColumns={this.processColumns}
                hasFreezableColumns
                frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                pinTooltip={t('pinTooltip')}
                unpinTooltip={t('unpinTooltip')}
            />
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isLoading = LineItemSelectors.isEntitiesLoading(state, ownProps);
    const data = LineItemSelectors.getEntities(state, ownProps);
    const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
    const isExportEntityActive = isExportEntityActiveSelector(state, ownProps);
    const isImportEntityActive = isImportEntityActiveSelector(state, ownProps);
    const entityIdToEdit = editEntityIdSelector(state, ownProps);
    const entitySettings = getEntitySettings(state, ownProps);
    const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
    const skills = SkillSelectors.getEntities(state, ownProps);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

    return {
        clientId,
        isLoading,
        data,
        isAddEntityActive,
        entityIdToEdit,
        entitySettings,
        isExportEntityActive,
        isImportEntityActive,
        tableSettings,
        skills,
        isGlobalPersonnel
    };
};

const withRedux = connect(mapStateToProps, {
    fetchEntities: LineItemActions.fetchActiveEntities,
    fetchSkills: SkillActions.fetchEntities,
    disableEntity: LineItemActions.disableEntity,
    clearData: LineItemActions.clearData,
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
    bulkDisableEntity: LineItemActions.bulkDisableEntity
});

const enhance = compose(
    themr(null, theme),
    themr(null, specificTheme),
    withRouter,
    withUnitConverter,
    withRedux,
    translate('entityManagement'),
    MasterContainerHOC
);

const ThemedConnectedLineItemMasterContainer = enhance(LineItemMasterContainer);

export default ThemedConnectedLineItemMasterContainer;
